import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { UserMaintenanceService } from '../../_services/user-maintenance/user-maintenance.service';
import { ServiceProcessService } from '../../_services/service-process/service-process.service';
import { MaintenanceRole, UpdateUserProfileRequest, UserDetail } from '../../_models/admin/user-maintenance.models';
import { hasRole } from '../../_helpers/can-use/current-user-roles';

/** The kill switch that gates directory provisioning (ServiceProcessKeys.IdentityProvision). */
const IDENTITY_PROVISION_KEY = 'identity.provision';

/** dbo.RoleType of the client roles (CLIENT, CLREPORTS, CUSTODIAN, ASSESSOR): set per client on Client Users, not here. */
const CLIENT_ROLE_TYPE_ID = 3;

/**
 * Admin edit of a single user. Three simple tabs: Details (profile), Roles (assign/unassign) and
 * Security (MFA required, activation status, resend activation, password reset). Gated on
 * UserMaintenanceEdit. Roles and Security are ADMIN only: anyone else gets Details, with resend activation and
 * password reset beneath it, and no changes at all to an ADMIN account. The API enforces the same.
 */
@Component({
  selector: 'user-edit',
  templateUrl: 'user-edit.page.html',
  standalone: false
})
export class UserEditPage implements OnInit {

  public userId: number;
  public detail: UserDetail;
  /** What the role picker offers: every role but the client roles, apart from any the user already holds. */
  public roles: MaintenanceRole[] = [];
  private allRoles: MaintenanceRole[] = [];
  public activeTab: 'details' | 'roles' | 'security' = 'details';

  /** The signed-in user holds ADMIN: the Roles and Security tabs, and ADMIN accounts, are theirs alone. */
  public isAdmin: boolean = false;

  // Details form
  public firstName: string = '';
  public lastName: string = '';
  public email: string = '';
  public mobileNumber: string = '';
  public isInternal: boolean = false;
  public mfaEnabled: boolean = false;

  // Roles
  public selectedRoleIds: number[] = [];
  private originalRoleIds: number[] = [];

  public savingProfile: boolean = false;
  public savingRoles: boolean = false;

  // Directory identity. Provisioning is gated by the identity.provision switch, read from the same
  // service-process flags the admin System Controls screen edits — the button says why it is
  // unavailable rather than failing when pressed.
  public identityProvisionEnabled: boolean = false;
  public changingIdentity: boolean = false;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private featureHelperService: FeatureHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private service: UserMaintenanceService,
    private serviceProcessService: ServiceProcessService,
    private dialog: MatDialog,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService
  ) { }

  ngOnInit(): void {
    const perms = this.featureHelperService.checkFeaturePermissions(['UserMaintenanceEdit']);
    const editPerm = perms.find(f => f.feature === 'UserMaintenanceEdit');
    if (!editPerm || !editPerm.enabled) {
      console.error('User does not have the required permissions to edit users');
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
      this.authenticationStateHelper.unauthorizedToken = true;
      return;
    }

    this.userId = Number(this.route.snapshot.paramMap.get('id'));
    this.isAdmin = hasRole('ADMIN');

    this.loadDetail();

    // Roles and the identity.provision switch serve the Roles and Security tabs, which are ADMIN only.
    if (!this.isAdmin) {
      return;
    }

    this.service.getRoles().subscribe({
      next: roles => {
        this.allRoles = roles ?? [];
        this.refreshRoleOptions();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });

    // A failed flag read leaves provisioning shown as unavailable rather than blocking the screen:
    // the server enforces the switch regardless, so the worst case is a button the admin cannot press.
    this.serviceProcessService.getProcesses().subscribe({
      next: flags => {
        const flag = (flags || []).find(f => f.processKey === IDENTITY_PROVISION_KEY);
        this.identityProvisionEnabled = !!flag && flag.isEnabled;
      },
      error: () => this.identityProvisionEnabled = false
    });
  }

  /** The account may be changed here: anyone with this page may change a non-ADMIN account, only ADMIN an ADMIN one. */
  public get canChange(): boolean {
    const targetIsAdmin = (this.detail?.roles ?? []).some(r => (r.roleCode ?? '').toUpperCase() === 'ADMIN');
    return this.isAdmin || !targetIsAdmin;
  }

  private loadDetail(): void {
    this.service.getUserDetail(this.userId).subscribe({
      next: (detail: UserDetail) => {
        this.detail = detail;
        this.firstName = detail.firstName;
        this.lastName = detail.lastName;
        this.email = detail.email;
        this.mobileNumber = detail.mobileNumber;
        this.isInternal = detail.isInternal;
        this.mfaEnabled = detail.options ? detail.options.mfaEnabled : false;
        this.originalRoleIds = (detail.roles || []).map(r => r.roleId);
        this.selectedRoleIds = [...this.originalRoleIds];
        this.refreshRoleOptions();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  /** The user is linked to a client or holds a client role, so the Roles tab points to Client Users. */
  public get showClientRolesNote(): boolean {
    return !!this.detail?.hasClientLinks
      || (this.detail?.roles ?? []).some(r => r.roleTypeId === CLIENT_ROLE_TYPE_ID);
  }

  /**
   * Client roles are not offered: they are set per client on Client Users. One the user already holds on the whole
   * user stays in the list, so it shows and can be removed.
   */
  private refreshRoleOptions(): void {
    const held = new Set(this.originalRoleIds);
    this.roles = this.allRoles.filter(r => r.roleTypeId !== CLIENT_ROLE_TYPE_ID || held.has(r.id));
  }

  public setTab(tab: 'details' | 'roles' | 'security'): void {
    this.activeTab = tab;
  }

  public saveProfile(): void {
    if (this.savingProfile) {
      return;
    }
    const request: UpdateUserProfileRequest = {
      id: this.userId,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      mobileNumber: this.mobileNumber,
      isInternal: this.isInternal,
      mfaEnabled: this.mfaEnabled
    };
    this.savingProfile = true;
    this.service.updateProfile(request).subscribe({
      next: () => {
        this.savingProfile = false;
        this.successModalHelper.show('User profile updated');
        this.loadDetail();
      },
      error: error => {
        this.savingProfile = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  public saveRoles(): void {
    if (this.savingRoles) {
      return;
    }
    const currentSet = new Set(this.selectedRoleIds);
    const originalSet = new Set(this.originalRoleIds);
    const toAdd = this.selectedRoleIds.filter(id => !originalSet.has(id));
    const toRemove = this.originalRoleIds.filter(id => !currentSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.successModalHelper.show('No role changes to save');
      return;
    }

    const calls = [
      ...toAdd.map(roleId => this.service.assignRole({ userId: this.userId, roleId })),
      ...toRemove.map(roleId => this.service.unassignRole({ userId: this.userId, roleId }))
    ];

    this.savingRoles = true;
    forkJoin(calls).subscribe({
      next: () => {
        this.savingRoles = false;
        this.successModalHelper.show('Roles updated');
        this.loadDetail();
      },
      error: error => {
        this.savingRoles = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  public toggleMfaRequired(checked: boolean): void {
    this.service.setMfaRequired(this.userId, checked).subscribe({
      next: () => {
        this.successModalHelper.show('MFA setting updated');
        this.loadDetail();
      },
      error: error => this.errorModalHelper.show('Could not update MFA. Please try again.', error)
    });
  }

  public resendActivation(): void {
    this.service.resendActivation(this.userId).subscribe({
      next: () => this.successModalHelper.show('New activation code has been sent'),
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  public sendPasswordReset(): void {
    if (!this.detail) {
      return;
    }
    this.service.sendPasswordReset(this.detail.email, this.detail.applicationId).subscribe({
      next: () => this.successModalHelper.show('Password reset link has been sent'),
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  /**
   * Creates a directory identity for this user so they can sign in through Entra as well as on the
   * login form. Sends no email. A refused provision comes back as success:false with the provider's
   * message — shown as an error rather than silently doing nothing.
   */
  public provisionIdentity(): void {
    if (this.changingIdentity) {
      return;
    }
    this.changingIdentity = true;
    this.service.provisionIdentity(this.userId).subscribe({
      next: result => {
        this.changingIdentity = false;
        if (result.success) {
          this.successModalHelper.show(result.message);
          this.loadDetail();
        } else {
          this.errorModalHelper.show(result.message, { status: result.statusCode });
        }
      },
      error: error => {
        this.changingIdentity = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  /**
   * Clears the link to the directory account. The account itself is left alone and the next Entra
   * sign-in re-links by address, so this is recoverable — but confirm it anyway, because a user with
   * no password has nothing else to sign in with.
   */
  public unlinkIdentity(): void {
    if (this.changingIdentity || !this.detail) {
      return;
    }

    const message = this.detail.hasPassword
      ? 'Unlink the Entra identity for ' + this.detail.firstName + ' ' + this.detail.lastName +
        '? They will still be able to sign in with their password.'
      : 'Unlink the Entra identity for ' + this.detail.firstName + ' ' + this.detail.lastName +
        '? They have no password, so they will not be able to sign in at all.';

    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: message }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.changingIdentity = true;
        this.service.unlinkIdentity(this.userId).subscribe({
          next: result => {
            this.changingIdentity = false;
            this.successModalHelper.show(result.message);
            this.loadDetail();
          },
          error: error => {
            this.changingIdentity = false;
            this.errorModalHelper.show(error.message, error);
          }
        });
      });
  }

  public back(): void {
    this.router.navigate(['/user-maintenance']);
  }
}
